/**
 * Grand Quack Auto — HUD overlay.
 *
 * DOM-based UI: chaos bar with wanted stars, timer, and objective text.
 * Runs as a System with a render() hook that updates DOM each frame.
 * Lazy-initializes DOM refs on first render call.
 *
 * See research.md §5 session 5 prompt.
 */

import { DUCKLING as DUCKLING_CFG, WANTED_THRESHOLDS } from '../config';
import { getDucklings, getRageRemaining, isMotherEnraged } from '../systems/ducklingSystem';
import { getCurrentObjective, isAllComplete, justCompleted } from '../systems/objectiveSystem';
import { updateTicker } from './newsTicker';
import type { System, World } from '../types';

// ── DOM refs (cached on first render) ─────────────────────────────────────

let initialized = false;
let chaosEl: HTMLElement | null = null;
let timerEl: HTMLElement | null = null;
let objectiveEl: HTMLElement | null = null;
let chaosFill: HTMLElement | null = null;
let chaosLabel: HTMLElement | null = null;
let starsEl: HTMLElement | null = null;
let broodEl: HTMLElement | null = null;
let rageEl: HTMLElement | null = null;

const FILL_COLORS = ['#ff4444', '#ff8822', '#ff2266', '#aa22ff'] as const;

function initDom(): void {
  initialized = true;
  chaosEl = document.getElementById('hud-chaos');
  timerEl = document.getElementById('hud-timer');
  objectiveEl = document.getElementById('hud-objective');

  if (chaosEl) {
    chaosEl.innerHTML = [
      '<span class="hud-stars" id="wanted-stars"></span>',
      '<span id="chaos-label">0</span>',
      '<div id="chaos-bar"><div id="chaos-fill"></div></div>',
      '<span id="hud-brood" style="margin-left:14px; font-weight:bold; color:#ffd700;">🐥 ×0</span>',
      '<span id="hud-rage" style="margin-left:10px; padding:2px 8px; border-radius:4px; background:#ff2233; color:#fff; font-weight:bold; display:none;">🔥 RAGE 0.0s</span>',
    ].join('');
    chaosFill = document.getElementById('chaos-fill');
    chaosLabel = document.getElementById('chaos-label');
    starsEl = document.getElementById('wanted-stars');
    broodEl = document.getElementById('hud-brood');
    rageEl = document.getElementById('hud-rage');
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ── System ────────────────────────────────────────────────────────────────

export const hudSystem: System = {
  name: 'hud',
  render(_dt: number, _alpha: number, world: World) {
    if (!initialized) initDom();
    updateTicker(world);

    // ── Chaos bar ──────────────────────────────────────────────────
    if (chaosFill && chaosLabel && starsEl) {
      const pct = Math.min(100, (world.chaos.points / WANTED_THRESHOLDS.chaosDuck) * 100);
      chaosFill.style.width = `${pct}%`;
      chaosFill.style.background = FILL_COLORS[world.chaos.wanted] ?? FILL_COLORS[0];
      chaosLabel.textContent = String(Math.floor(world.chaos.points));
      starsEl.textContent = world.chaos.wanted > 0 ? '\u2605'.repeat(world.chaos.wanted) + ' ' : '';
    }

    // ── Brood + Mother's Rage ──────────────────────────────────────
    if (broodEl) {
      const n = getDucklings().length;
      broodEl.textContent = `🐥 ×${n}/${DUCKLING_CFG.maxBrood}`;
    }
    if (rageEl) {
      if (isMotherEnraged(world)) {
        rageEl.style.display = '';
        rageEl.textContent = `🔥 MOTHER'S RAGE ${getRageRemaining(world).toFixed(1)}s`;
      } else {
        rageEl.style.display = 'none';
      }
    }

    // ── Timer ──────────────────────────────────────────────────────
    if (timerEl) {
      timerEl.textContent = formatTime(world.time.elapsed);
    }

    // ── Objective ──────────────────────────────────────────────────
    if (objectiveEl) {
      if (isAllComplete()) {
        objectiveEl.textContent = 'LEVEL COMPLETE!';
        objectiveEl.className = 'complete';
      } else if (justCompleted(world.time.elapsed)) {
        objectiveEl.textContent = '\u2713 COMPLETE!';
        objectiveEl.className = 'flash';
      } else {
        const obj = getCurrentObjective();
        objectiveEl.textContent = obj ? `\u25B8 ${obj.description}` : '';
        objectiveEl.className = '';
      }
    }
  },
};
